import React, { useState, useEffect, useContext } from "react";
import { API_URL } from "../../components/api";
import { formatDateTime } from "../../../utils/dateFormat";
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarExport,
    GridFooterContainer,
    GridPagination,
} from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import ExcelSVG from "./ExcelSVG.svg";
import FilterSVG from "./FilterSVG.svg";
import * as XLSX from "xlsx";
import Checkbox from "@mui/material/Checkbox";
import CustomizedActions from "./BulkActions";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import AuthContext from "../../context/AuthContext";
import axios from "axios";

import Notes from "./notes";
import ReceiptTable from "./ReceiptsTable";
import Notification from "./SendNotification";
import Cards from "./labels";
import Statement from "./statement";
import "./report.css";
import { sortByPaymentDate } from "./paymentDateSort";

function ZIAD() {
    const { user } = useContext(AuthContext);

    // --- State Management ---
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [Client, setClient] = useState("");
    const [Fullname, setFullname] = useState("");

    const [Cl, setCl] = useState("");
    const [checkedRows, setCheckedRows] = useState([]);
    const [show, setShow] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);

    const [showCollectedModal, setShowCollectedModal] = useState(false);
    const [showPostponeModal, setShowPostponeModal] = useState(false);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

    // --- Modal Control Functions ---
    const CloseCollect = () => setShow(false);
    const ShowCollect = () => setShow(true);

    const CloseNotification = () => setShowNotification(false);
    const ShowNotification = () => setShowNotification(true);

    const CloseReceipt = () => setShowReceipt(false);
    const ShowReceipt = () => setShowReceipt(true);

    const ClosePostponeModal = () => setShowPostponeModal(false);
    const ShowPostponeModal = (sub_id) => {
        setSelectedSubscriptionId(sub_id);
        setShowPostponeModal(true);
    };

    const CloseCollectedModal = () => setShowCollectedModal(false);
    const ShowCollectedModal = (sub_id) => {
        setSelectedSubscriptionId(sub_id);
        setShowCollectedModal(true);
    };

    const [showStatement, setShowStatement] = useState(false);

    const CloseStatement = () => setShowStatement(false);
    const ShowStatement = () => setShowStatement(true);

    // --- Filter Configuration ---
    const [filters, setFilters] = useState({
        status: "",
        fullname_en: "",
        suffix: "",
        balance_usd: "",
        balance: "",
        client_notes: "",
        phone_number: "",
        village_name: "",
        order: "",
        address_string: "",
        payment_date: "",
        due: "",
        expiration_date: "",
        collector: "",
        label: "",
        labels: "",
        note_details: "",
    });
    const [showFilterModal, setShowFilterModal] = useState(false);

    const [searchInput, setSearchInput] = useState("");
    const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

    // --- API Confirmation Handlers ---
    const handlePostponeConfirm = async () => {
        try {
            const authTokens = JSON.parse(localStorage.getItem("authTokens"));
            const response = await axios.post(
                `${API_URL}/accounting/info/Postpone/`,
                {
                    action: "postpone",
                    sub_id: selectedSubscriptionId,
                    username: user.username,
                },
                {
                    headers: { Authorization: `Bearer ${authTokens.access}` },
                }
            );

            if (response.status === 200) {
                fetchData();
                setShowPostponeModal(false);
            }
        } catch (error) {
            console.error("Error during postponement:", error);
        }
    };

    const handleCollectedConfirm = async () => {
        try {
            const authTokens = JSON.parse(localStorage.getItem("authTokens"));
            const response = await axios.post(
                `${API_URL}/accounting/info/Collected/`,
                {
                    action: "toBeCollected",
                    sub_id: selectedSubscriptionId,
                    username: user.username,
                },
                {
                    headers: { Authorization: `Bearer ${authTokens.access}` },
                }
            );
            if (response.status === 200) {
                fetchData();
                setShowCollectedModal(false);
            }
        } catch (error) {
            console.error("Error during collection update:", error);
        }
    };

    // --- Data Fetching Logic ---
    const fetchData = async () => {
        try {
            const authTokens = JSON.parse(localStorage.getItem("authTokens"));
            const response = await axios.get(`${API_URL}/accounting/info/ZIAD/`, {
                headers: { Authorization: `Bearer ${authTokens.access}` },
                params: { username: user.username },
            });

            if (response.status === 200) {
                setData(sortByPaymentDate(Array.isArray(response.data) ? response.data : []));
            }
        } catch (error) {
            console.error("Fetch data error:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [user.username]);

    // --- Table Styling and Pagination ---
    const getStatusCellStyle = (params) => {
        let statusColor = "";
        switch (params.value) {
            case "offline": statusColor = "status-offline"; break;
            case "online": statusColor = "status-online"; break;
            case "inactive": statusColor = "status-inactive"; break;
            case "expired": statusColor = "status-expired"; break;
            case "hold": statusColor = "status-hold"; break;
            case "HSI": statusColor = "status-hsi"; break;
            default: break;
        }
        return statusColor;
    };

    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(0);

    const paginatedRows = () => {
        const start = currentPage * pageSize;
        const end = start + pageSize;
        return filteredRows.slice(start, end);
    };

    const areAllRowsSelected = () => {
        const currentViewRows = paginatedRows();
        const currentViewIds = currentViewRows.map((row) => row.subscription_id);
        return currentViewIds.every((id) => checkedRows.includes(id));
    };

    const handleSelectAllCheckboxes = () => {
        const currentViewRows = paginatedRows();
        const currentViewIds = currentViewRows.map((row) => row.subscription_id);

        if (areAllRowsSelected()) {
            setCheckedRows(checkedRows.filter((id) => !currentViewIds.includes(id)));
        } else {
            setCheckedRows([
                ...checkedRows,
                ...currentViewIds.filter((id) => !checkedRows.includes(id)),
            ]);
        }
    };

    const handleCheckboxChange = (event, subscriptionId) => {
        const checked = event.target.checked;
        setCheckedRows((prev) => checked ? [...prev, subscriptionId] : prev.filter((id) => id !== subscriptionId));
    };

    const handleSearchChange = (event) => {
        const query = event.target.value;
        setSearchInput(query);
        sessionStorage.setItem("searchSpecial", query);
        setCurrentPage(0);
        if (searchFilterTimeout) clearTimeout(searchFilterTimeout);
        const newTimeout = setTimeout(() => setSearchQuery(query), 100);
        setSearchFilterTimeout(newTimeout);
    };

    // --- Filtering Logic: backend already returns Ziad rows; frontend only applies search + modal filters ---
    const filteredRows = Array.isArray(data) ? data.filter((row) => {
        const initialBalance = Number(row.balance);
        const initialBalanceMatch = (
            Number.isFinite(initialBalance)
            && initialBalance >= 0
        );

        const searchMatch = searchQuery.split(" ").every((word) =>
            Object.values(row).some((val) =>
                String(val).toLowerCase().includes(word.toLowerCase().trim())
            )
        );

        const modalFilterMatch = Object.keys(filters).every((key) => {
            if (!filters[key]) return true;
            return String(row[key] || "").toLowerCase().includes(String(filters[key]).toLowerCase());
        });

        return initialBalanceMatch && searchMatch && modalFilterMatch;
    }) : [];

    // --- Column Definitions (Identical to Imad's) ---
    const columns = [
        {
            field: "checkbox",
            headerName: "",
            width: 100,
            renderHeader: () => (
                <Checkbox
                    indeterminate={checkedRows.length > 0 && checkedRows.length < filteredRows.length}
                    checked={checkedRows.length === filteredRows.length && filteredRows.length > 0}
                    onChange={() => {
                        if (checkedRows.length === filteredRows.length) {
                            setCheckedRows([]);
                        } else {
                            setCheckedRows(filteredRows.map((row) => row.subscription_id));
                        }
                    }}
                />
            ),
            renderCell: (params) => (
                <Checkbox
                    className="subcheck"
                    checked={checkedRows.includes(params.row.subscription_id)}
                    onChange={(event) => {
                        const checked = event.target.checked;
                        setCheckedRows((prev) =>
                            checked ? [...prev, params.row.subscription_id] : prev.filter((id) => id !== params.row.subscription_id)
                        );
                    }}
                />
            ),
        },
        { field: "status", headerName: "Status", flex: 2, minWidth: 100, cellClassName: (params) => getStatusCellStyle(params) },
        { field: "fullname_en", headerName: "Full Name", flex: 2, minWidth: 250 },
        { field: "suffix", headerName: "Suffix", flex: 2, minWidth: 100 },
        { field: "balance_usd", headerName: "Balance (USD)", flex: 1, minWidth: 150 },
        { field: "balance", headerName: "Initial Balance", flex: 1, minWidth: 150 },
        { field: "client_notes", headerName: "Client Notes", flex: 1, minWidth: 150 },
        {
            field: "phone_number",
            headerName: "Phone Number",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                let phone = params.row.phone_number;
                if (phone && phone.length === 7) phone = "0" + phone;
                return <span>{phone}</span>;
            }
        },
        { field: "village_name", headerName: "Village", flex: 1, minWidth: 150 },
        { field: "order", headerName: "Village Order", flex: 1, minWidth: 150 },
        { field: "address_string", headerName: "Address", flex: 1, minWidth: 250 },
        {
            field: "payment_date",
            headerName: "Payment Date",
            flex: 1,
            minWidth: 150,
            valueFormatter: (params) => {
                const date = new Date(params.value);
                return isNaN(date.getTime()) ? params.value : formatDateTime(date);
            }
        },
        { field: "due", headerName: "Due", flex: 1, minWidth: 150 },
        {
            field: "expiration_date",
            headerName: "Expiration Date",
            flex: 1,
            minWidth: 150,
            valueFormatter: (params) => {
                const date = new Date(params.value);
                return isNaN(date.getTime()) ? params.value : formatDateTime(date);
            }
        },
        { field: "collector", headerName: "Collector", flex: 2, minWidth: 150 },
        { field: "label", headerName: "MC Category", flex: 2, minWidth: 100 },
        {
            field: "labels",
            headerName: "Labels",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => <Cards sub_id={params.row.subscription_id} />
        },
        {
            field: "note_details",
            headerName: "Latest Note",
            flex: 2,
            minWidth: 250,
            valueGetter: (params) => {
                const { note_user, latest_note } = params.row;
                return note_user && latest_note ? `${note_user}: ${latest_note}` : "No Note";
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 700,
            flex: 2,
            renderCell: (params) => (
                <div>
                    <button className="edit-client" onClick={() => { setClient(params.row.subscription_id); setFullname(params.row.fullname_en); ShowCollect(); }}>Notes</button>
                    <button className="edit-client" onClick={() => { setCl(params.row.client_id); ShowReceipt(); }}>Receipt</button>
                    <button className="edit-client" onClick={() => { setClient(params.row.subscription_id); setFullname(params.row.fullname_en); ShowNotification(); }}>Notifications</button>
                    <button className="edit-client postpone-btn" onClick={() => ShowPostponeModal(params.row.subscription_id)}>Postpone</button>
                    <button className="edit-client postpone-btn" onClick={() => ShowCollectedModal(params.row.subscription_id)}>To Be Collected</button>
                    <button onClick={() => { ShowStatement(); setCl(params.row.client_id); }} className="edit-client">Statement</button>
                </div>
            ),
        },
    ];

    const getRowId = (row) => row.subscription_id;

    // --- Excel Export Setup ---
    const columnHeaders = {
        status: "Status",
        fullname_en: "Full Name",
        suffix: "Suffix",
        balance_usd: "Balance (USD)",
        balance: "Initial Balance",
        client_notes: "Client Notes",
        payment_date: "Payment Date",
        due: "Due",
        expiration_date: "Expiration Date",
        collector: "Collector",
        label: "Label",
        note_details: "Latest Note",
    };

    const handleExportToExcel = () => {
        const numericKeys = new Set([
            "balance_usd",
            "balance",
        ]);

        // Export the same valid Ziad rows while excluding
        // rows with a negative Initial Balance.
        const exportRows = filteredRows.filter(row => {
            const value = row.balance;

            if (value == null || value === "") {
                return true;
            }

            const numericValue = Number(value);

            return (
                !Number.isFinite(numericValue) ||
                numericValue >= 0
            );
        });

        const transformedRows = exportRows.map(row => {
            const obj = {};

            Object.keys(columnHeaders).forEach(key => {
                const value = row[key];

                if (numericKeys.has(key)) {
                    if (value == null || value === "") {
                        obj[columnHeaders[key]] = "";
                        return;
                    }

                    const numericValue = Number(value);

                    obj[columnHeaders[key]] =
                        Number.isFinite(numericValue)
                            ? numericValue
                            : "";

                    return;
                }

                // Preserve text identifiers such as phone
                // numbers, including leading zeroes.
                obj[columnHeaders[key]] = value ?? "";
            });

            return obj;
        });

        const worksheet =
            XLSX.utils.json_to_sheet(transformedRows);

        const numericHeaders = [
            "Balance (USD)",
            "Initial Balance",
        ];

        numericHeaders.forEach(header => {
            const columnIndex =
                Object.values(columnHeaders).indexOf(header);

            if (columnIndex === -1) {
                return;
            }

            for (
                let rowIndex = 1;
                rowIndex <= transformedRows.length;
                rowIndex += 1
            ) {
                const cellAddress = XLSX.utils.encode_cell({
                    r: rowIndex,
                    c: columnIndex,
                });

                if (worksheet[cellAddress]) {
                    worksheet[cellAddress].z = "0.00";
                }
            }
        });

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Ziad_Only"
        );

        XLSX.writeFile(
            workbook,
            "Ziad_Collector_Report.xlsx"
        );
    };

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <Button className="ziad-toolbar-action" onClick={() => setShowFilterModal(true)} variant="outline-primary" style={{ border: "none" }}>
                <img src={FilterSVG} alt="Filter" style={{ width: "18px", marginRight: "8px" }} /> Filter
            </Button>
            <GridToolbarExport />
            <Button className="ziad-toolbar-action" onClick={handleExportToExcel} variant="outline-success" style={{ border: "none" }}>
                <img src={ExcelSVG} alt="Excel" style={{ width: "20px", marginRight: "8px" }} /> Excel
            </Button>
        </GridToolbarContainer>
    );

    return (
        <div className="InternetTable ziad-report-table">
            <div className="actions">
                <CustomizedActions subscriptions={checkedRows} deselect={() => setCheckedRows([])} fetch={fetchData} />
                <input type="text" placeholder="Search in Ziad data..." value={searchInput} onChange={handleSearchChange} className="search-client" />
            </div>
            {!loading && (
                <div style={{ height: 570, width: "100%" }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        getRowId={getRowId}
                        components={{
                            Footer: () => (
                                <GridFooterContainer>
                                    <Typography style={{ marginLeft: "16px", color: "white" }}>
                                        {`Total Ziad Records: ${filteredRows.length} | Selected: ${checkedRows.length}`}
                                    </Typography>
                                    <GridPagination />
                                </GridFooterContainer>
                            ),
                            Toolbar: CustomToolbar,
                        }}
                        pageSize={pageSize}
                        onPageSizeChange={(s) => setPageSize(s)}
                        pagination
                    />
                </div>
            )}

            {/* Modals Section - All Identical to Imad's */}
            <Modal show={showPostponeModal} onHide={ClosePostponeModal}>
                <Modal.Header closeButton><Modal.Title>Confirm Postpone</Modal.Title></Modal.Header>
                <Modal.Body>Are you sure you want to postpone this subscription?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={ClosePostponeModal}>No</Button>
                    <Button variant="primary" onClick={handlePostponeConfirm}>Yes</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={show} onHide={CloseCollect} className="modal-lg">
                <div className="forma">
                    <Modal.Header closeButton><Modal.Title>{Fullname} - Notes</Modal.Title></Modal.Header>
                    <Notes sub_id={Client} />
                </div>
            </Modal>

            <Modal show={showReceipt} onHide={CloseReceipt} className="modal-xl">
                <div className="forma">
                    <Modal.Header closeButton><Modal.Title>Receipts History</Modal.Title></Modal.Header>
                    <ReceiptTable client_id={Cl} />
                </div>
            </Modal>

            <Modal show={showNotification} onHide={CloseNotification} className="modal-lg">
                <div className="forma">
                    <Modal.Header closeButton><Modal.Title>Send Notification to {Fullname}</Modal.Title></Modal.Header>
                    <Notification sub_id={Client} />
                </div>
            </Modal>

            <Modal show={showStatement} onHide={CloseStatement} className="modal-xl">
                <div className="forma">
                    <Modal.Header closeButton><Modal.Title>Account Statement</Modal.Title></Modal.Header>
                    <Statement client_id={Cl} />
                </div>
            </Modal>

            <Modal show={showCollectedModal} onHide={CloseCollectedModal}>
                <Modal.Header closeButton><Modal.Title>Confirm Collection</Modal.Title></Modal.Header>
                <Modal.Body>Mark this as "To Be Collected"?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={CloseCollectedModal}>Cancel</Button>
                    <Button variant="primary" onClick={handleCollectedConfirm}>Confirm</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ZIAD;